// src/oura/hooks/useScanAudit.ts
//
// The automatic audit of the scan on screen (ourteam-backend#5910, #4242).
//
// Staff only. `enabled` is false for a client, a sample business and a screen
// with no scan, and the endpoint is then never called: a client login is refused
// by the backend, and the badge is never mounted for one.
//
// The audit is NOT there the moment a scan completes: the backend starts it in
// a background thread and its watcher gives up after 300 seconds (then stores
// `error`). Until it writes its result the endpoint says `not_audited`, so one
// read just after a scan finishes would leave "Not audited" on screen for good.
// The hook therefore asks again while the answer is `not_audited`, but only for
// a scan that finished recently (`shouldPollAudit`).

import { useEffect, useState } from "react";
import { getScanAudit, type AeoScanAudit } from "@service/aeo";
import { AUDIT_POLL_MS, auditFor, shouldPollAudit, type AuditResult } from "../lib/scanAudit";

/**
 * The audit, or null while it loads, when it is not asked for, and when it cannot
 * be read. `scanAt` is when the scan finished.
 */
export function useScanAudit(
    assessmentId: string | undefined,
    enabled: boolean,
    scanAt?: string | null,
): AeoScanAudit | null {
    const [result, setResult] = useState<AuditResult | null>(null);

    useEffect(() => {
        if (!enabled || !assessmentId) return;
        let live = true;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const load = async () => {
            const audit = await getScanAudit(assessmentId);
            if (!live) return;
            setResult({ assessmentId, audit });
            if (shouldPollAudit(audit?.status, scanAt, Date.now())) {
                timer = setTimeout(() => void load(), AUDIT_POLL_MS);
            }
        };
        void load();

        return () => {
            live = false;
            if (timer) clearTimeout(timer);
        };
    }, [assessmentId, enabled, scanAt]);

    // Scoped at render time rather than cleared in an effect: for the one commit
    // after the scan changes, the previous scan's result must not render.
    return auditFor(result, assessmentId, enabled);
}
